import { FC, useEffect } from 'react';

import { useTranslations } from '@lang';
import { required } from '@utils/validators';
import { Container } from 'styled-bootstrap-grid';

import { Guest } from 'shared-types/Common';
import { Button, InfoBox, StickyBarContent } from '../../components';
import Portal from '../../components/Portal';

import { GuestDetailsStepProps } from './GuestDetails.types';

import { AddGuestsForm } from './AddGuestsForm';

export const GuestDetails: FC<GuestDetailsStepProps> = ({
  spaEntity,
  typeName = 'Spa Treatment',
  stickyBarId,
  handleSubmit,
  valid,
  submitting,
  existingGuests = [],
  selectedGuests,
  selectedArrivalTime,
  handleInvalidConfig,
  guest = null,
  showGenderNeutral,
  isItemFetching,
  change,
}) => {
  const { t } = useTranslations();

  useEffect(() => {
    if (!selectedGuests) {
      handleInvalidConfig();
    }
  }, [selectedGuests, handleInvalidConfig]);

  const getCalculatedPrice = () => ({
    ...selectedArrivalTime.price,
    amount: selectedArrivalTime.price.amount * selectedGuests.length,
  });

  const getCalculatedDiscount = () =>
    selectedArrivalTime.discount
      ? {
          discountedPrice: {
            ...selectedArrivalTime.discount?.discountedPrice,
            amount: selectedArrivalTime.discount?.discountedPrice.amount * selectedGuests.length,
          },
        }
      : undefined;

  const { availability, extras, minimumGuests, selectionGroups, maximumGuests } = spaEntity || {};
  const timeslotMaxGuests =
    selectedArrivalTime && availability
      ? availability?.find(
          (arrivalTime) => selectedArrivalTime.startDateTime === arrivalTime.startDateTime
        )
      : null;

  const maxGuests = maximumGuests ?? minimumGuests ?? 0;
  const numberAvailable = timeslotMaxGuests?.numberAvailable ?? maxGuests;
  const effectiveMaxGuests = Math.min(numberAvailable, maxGuests);

  const isExtrasAvailable = extras?.length || selectionGroups?.length;
  const hasEnoughQuantity =
    !spaEntity || (!!spaEntity && effectiveMaxGuests >= (minimumGuests ?? 0));

  const guestDataValid = selectedGuests?.reduce((prev: any, cur: Guest) => {
    if (!prev) return false;
    if (
      required(cur.firstName) ||
      required(cur.lastName) ||
      required(cur.title) ||
      required(cur.gender)
    )
      return false;
    return true;
  }, true);

  return selectedArrivalTime ? (
    <form onSubmit={handleSubmit} noValidate>
      <Container fluid>
        <AddGuestsForm
          existingGuests={existingGuests}
          guest={guest}
          selectedGuests={selectedGuests}
          hasEnoughQuantity={hasEnoughQuantity}
          minimumGuests={minimumGuests}
          maximumGuests={effectiveMaxGuests}
          spaEntity={spaEntity}
          showGenderNeutral={showGenderNeutral}
          typeName={typeName}
          isItemFetching={isItemFetching}
          fieldChange={change}
        />
        {typeName === 'Spa Treatment' && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      </Container>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={spaEntity.name}
          displayedPrice={getCalculatedPrice() as any}
          discountedPrice={getCalculatedDiscount()}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit}
              disabled={!hasEnoughQuantity || !valid || submitting || !guestDataValid}
            >
              {!!isExtrasAvailable && t('treatment.customizeTreatmentsCTA')}
              {!isExtrasAvailable && t('treatment.addToBasketCTA')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
